import React, { useState } from 'react';
import { HealthScore } from '../../types';

interface HealthScoreCardProps {
  healthScore: HealthScore | null;
}

const HealthScoreCard: React.FC<HealthScoreCardProps> = ({ healthScore }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  if (!healthScore) return null;

  const score = healthScore.score || 0;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const getScoreClass = (score: number) => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  const getScoreMessage = (score: number) => {
    if (score >= 90) return 'Excellent! Keep it up!';
    if (score >= 80) return 'Great progress!';
    if (score >= 70) return 'Good momentum!';
    if (score >= 60) return 'Room for improvement';
    return 'Needs attention';
  };

  return (
    <div className="health-score-card">
      <div className="score-header">
        <h3>Health Score</h3>
        <button 
          className="info-button"
          onClick={() => setShowDetails(!showDetails)}
          aria-label="Toggle health score details"
        >
          ℹ️
        </button>
      </div>

      <div className="score-visualization">
        <svg width="120" height="120" className="score-svg">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={`var(--score-${getScoreClass(score)})`}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform="rotate(-90 60 60)"
            className="score-progress"
          />
          <text
            x="60"
            y="60"
            textAnchor="middle"
            dominantBaseline="middle"
            className="score-text"
          >
            <tspan x="60" dy="-5" className="score-number">{score}</tspan>
            <tspan x="60" dy="20" className="score-percent">%</tspan>
          </text>
        </svg>
      </div>

      <p className={`score-message ${getScoreClass(score)}`}>
        {getScoreMessage(score)}
      </p>

      {showDetails && healthScore.factors && (
        <div className="score-details">
          <h4>Score Breakdown</h4>
          <div className="factor-list">
            <div className="factor-item">
              <span className="factor-label">Progress</span>
              <div className="factor-bar">
                <div 
                  className="factor-fill"
                  style={{ width: `${healthScore.factors.progress}%` }}
                />
              </div>
              <span className="factor-value">{healthScore.factors.progress}%</span>
            </div>
            <div className="factor-item">
              <span className="factor-label">Time</span>
              <div className="factor-bar">
                <div 
                  className="factor-fill"
                  style={{ width: `${healthScore.factors.timeInOnboarding}%` }}
                />
              </div>
              <span className="factor-value">{healthScore.factors.timeInOnboarding}%</span>
            </div>
            <div className="factor-item">
              <span className="factor-label">Engagement</span>
              <div className="factor-bar">
                <div 
                  className="factor-fill"
                  style={{ width: `${healthScore.factors.engagement}%` }}
                />
              </div>
              <span className="factor-value">{healthScore.factors.engagement}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthScoreCard;